import React, { useState, useEffect } from 'react';
import SelectField from '../components/SelectField';
import { FaPlus, FaPen, FaTrash, FaTimes, FaSave, FaCalendarAlt, FaExclamationTriangle, FaInfoCircle, FaCheckCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import ConfirmationModal from '../components/ConfirmationModal';
import LoadingState from '../components/LoadingState';
import { 
  listCampaignsApi, 
  fetchCampaign, 
  fetchGenesysFilters,
  listScheduledJobsApi,
  createScheduledJobApi,
  deleteScheduledJobApi,
  updateScheduledJobApi
} from '../api/api';

const ScheduledJobs = ({ setAlert, username, userEmail }) => {
  const navigate = useNavigate();
  const DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  const [scheduledJobs, setScheduledJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  // One slot for whichever confirmation is pending — native window.confirm()
  // doesn't fit the app's design language, so this drives the themed
  // ConfirmationModal instead. { title, message, onConfirm }
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [jobName, setJobName] = useState('');
  const [creatingJob, setCreatingJob] = useState(false);
  const [selectedMinute, setSelectedMinute] = useState('0');
  const [selectedHour, setSelectedHour] = useState('8'); 
  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState('?'); 
  const [startDaysAgo, setStartDaysAgo] = useState(1); 
  const [endDaysAgo, setEndDaysAgo] = useState(0);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [, setLoadingCampaigns] = useState(true);
  const [scorecards, setScorecards] = useState([]);
  const [selectedScorecard, setSelectedScorecard] = useState(null);
  const [, setLoadingScorecards] = useState(false);
  const [fetchingFilters, setFetchingFilters] = useState(false);
  const [agentOptions, setAgentOptions] = useState([]);
  const [wrapUpCodeOptions, setWrapUpCodeOptions] = useState([]);
  const [queueOptions, setQueueOptions] = useState([]);
  const [agentSearch, setAgentSearch] = useState('');
  const [wrapupSearch, setWrapupSearch] = useState('');
  const [queueSearch, setQueueSearch] = useState('');
  const [isAgentOpen, setIsAgentOpen] = useState(false);
  const [isWrapupOpen, setIsWrapupOpen] = useState(false);
  const [isQueueOpen, setIsQueueOpen] = useState(false);
  const [divisionIdInput, setDivisionIdInput] = useState('');
  const start = parseInt(startDaysAgo, 10);
  const end = parseInt(endDaysAgo, 10);

  // A cleared/non-numeric field parses to NaN; every NaN comparison is false,
  // so without this flag the guardrail would report a "Perfect Match: NaN-day
  // window" and the schedule would submit lookback values of null.
  const hasInvalidNumbers = !Number.isFinite(start) || !Number.isFinite(end);
  const isInvalidRange = start < end;
  const windowSize = parseInt(startDaysAgo, 10) - parseInt(endDaysAgo, 10);
  const frequencyInDays = selectedDayOfWeek === '?' ? 1 : 7;
  
  const hasOverlapRisk = windowSize > frequencyInDays;
  const hasGapRisk = windowSize < frequencyInDays;
  const [editingJobId, setEditingJobId] = useState(null);

  // Per-agent sampling
  const [callsPerAgent, setCallsPerAgent] = useState('');
  const [samplingScope, setSamplingScope] = useState('range'); // 'range' or 'day'

  const [filters, setFilters] = useState({
    queueIds: [],
    direction: '',
    userIds: [],
    wrapUpCodes: [],
    ani: '',
    dnis: '',
    divisionIds: []
  });

  // Per-type: the filters endpoint fetches queues/agents/wrap-up codes
  // independently and can return some data with other types failing (e.g. a
  // Genesys permission granted for queues but not wrap-up codes).
  const [genesysFilterErrors, setGenesysFilterErrors] = useState({});

  useEffect(() => {
    fetchJobs();
    fetchCampaigns();
    loadGenesysFilters();
  }, []);

  useEffect(() => {
    if (!selectedCampaign) {
      setScorecards([]);
      setSelectedScorecard(null);
      return;
    }
    const loadScorecards = async () => {
      setLoadingScorecards(true);
      try {
        const res = await fetchCampaign(selectedCampaign.value);
        const names = res.templates || [];
        setScorecards(names.map((n) => ({ label: n, value: n })));
      } catch (err) {
        console.error('Error loading scorecards:', err);
      } finally {
        setLoadingScorecards(false);
      }
    };
    loadScorecards();
  }, [selectedCampaign]);

  const generateCronExpression = () => {
    const sastHour = parseInt(selectedHour, 10);
    
    // Convert SAST to UTC (-2)
    let utcHour = (sastHour - 2 + 24) % 24;
    
    let cronDow = selectedDayOfWeek;

    // Handle Day Wrap for the Cron Expression
    // If user picks 00:00 or 01:00 SAST, the UTC day is actually the PREVIOUS day
    if (selectedDayOfWeek !== '?') {
      if (sastHour < 2) {
        const currentIndex = DAYS.indexOf(selectedDayOfWeek);
        const prevIndex = (currentIndex - 1 + 7) % 7;
        cronDow = DAYS[prevIndex];
      }
    }

    const dom = cronDow !== '?' ? '?' : '*'; 
    return `cron(${selectedMinute} ${utcHour} ${dom} * ${cronDow} *)`;
  };


  const fetchJobs = async () => {
    setLoadingJobs(true);
    try {
      const res = await listScheduledJobsApi();
      setScheduledJobs(res || []);
    } catch (error) {
      setAlert({ variant: 'error', heading: 'Error', text: 'Failed to load scheduled jobs.' });
    } finally {
      setLoadingJobs(false);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const res = await listCampaignsApi();
      const items = res.campaigns || [];
      setCampaigns(items.map((c) => ({ label: c.campaignName, value: c.campaignName })));
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const loadGenesysFilters = async () => {
    setFetchingFilters(true);
    try {
      const res = await fetchGenesysFilters('all');
      if (res.success) {
        setQueueOptions(res.data.queues || []);
        setAgentOptions(res.data.agents || []);
        if (res.data.wrapUpCodes) {
          const mappedWrapup = res.data.wrapUpCodes.map(item => ({
            id: item.id || item.wrapupcodesId,
            name: item.name || item.Name
          }));
          setWrapUpCodeOptions(mappedWrapup);
        }
        setGenesysFilterErrors(res.errors || {});
      }
    } catch (error) {
      // The request itself failed (network, auth) rather than a specific
      // filter type — nothing came back, so all three pickers stay empty.
      console.error('Error loading Genesys filters:', error);
      setGenesysFilterErrors({
        queues: 'Could not load live Genesys data.',
        agents: 'Could not load live Genesys data.',
        wrapUpCodes: 'Could not load live Genesys data.',
      });
    } finally {
      setFetchingFilters(false);
    }
  };


  const updateFilter = (filterKey, value) => setFilters(prev => ({ ...prev, [filterKey]: value }));
  const removeFromArrayFilter = (filterKey, index) => {
    setFilters(prev => ({ ...prev, [filterKey]: prev[filterKey].filter((_, i) => i !== index) }));
  };
  const addToArrayFilter = (filterKey, value, inputSetter) => {
    if (value.trim()) {
      setFilters(prev => ({ ...prev, [filterKey]: [...prev[filterKey], value.trim()] }));
      inputSetter('');
    }
  };
  const clearAllFilters = () => {
    setFilters({ queueIds: [], direction: '', userIds: [], wrapUpCodes: [], ani: '', dnis: '', divisionIds: [] });
  };


  const handleCreateSchedule = () => {
    if (!jobName || !selectedCampaign || !selectedScorecard) {
      return setAlert({ variant: 'warning', heading: 'Missing Fields', text: 'Please fill out all required fields.' });
    }

    if (hasInvalidNumbers) {
      return setAlert({ variant: 'error', heading: 'Invalid Scan Window', text: 'Enter a number of days for both "Scan From" and "Scan Until".' });
    }

    if (isInvalidRange) {
      return setAlert({ variant: 'error', heading: 'Invalid Range', text: 'Scan "From" must be the same or a higher number than Scan "Until".' });
    }

    if (hasOverlapRisk) {
      setConfirmDialog({
        title: 'Overlapping Scan Window',
        message: `Your scan window (${windowSize} days) is larger than your run frequency (${frequencyInDays} days). This will cause duplicate records. Do you want to continue?`,
        onConfirm: doCreateSchedule,
      });
      return;
    }

    doCreateSchedule();
  };

  const doCreateSchedule = async () => {
    setConfirmDialog(null);
    if (parseInt(startDaysAgo) < parseInt(endDaysAgo)) {
      return setAlert({ variant: 'warning', heading: 'Invalid Range', text: 'Scan "From" must be a higher number than Scan "Until".' });
    }

    setCreatingJob(true);

    const activeFilters = {};
    if (filters.queueIds.length > 0) activeFilters.queueIds = filters.queueIds;
    if (filters.direction) activeFilters.direction = filters.direction;
    if (filters.userIds.length > 0) activeFilters.userIds = filters.userIds;
    if (filters.wrapUpCodes.length > 0) activeFilters.wrapUpCodes = filters.wrapUpCodes;
    if (filters.ani) activeFilters.ani = filters.ani;
    if (filters.dnis) activeFilters.dnis = filters.dnis;
    if (filters.divisionIds.length > 0) activeFilters.divisionIds = filters.divisionIds;

    const payload = {
      jobName,
      campaign: selectedCampaign.value,
      template: selectedScorecard.value,
      scheduleExpression: generateCronExpression(),
      filters: activeFilters,
      lookback: {
        startDaysAgo: parseInt(startDaysAgo, 10),
        endDaysAgo: parseInt(endDaysAgo, 10)
      },
      callsPerAgent: callsPerAgent ? parseInt(callsPerAgent, 10) : null,
      samplingScope,
      createdBy: userEmail
    };

    try {
      if (editingJobId) {
      // UPDATE MODE
        await updateScheduledJobApi(editingJobId, payload);
        setAlert({ variant: 'success', heading: 'Updated', text: 'Schedule updated successfully.' });
      } else {
        await createScheduledJobApi(payload);
        setAlert({ variant: 'success', heading: 'Success', text: 'Scheduled job created successfully.' });
      }
      
      setJobName('');
      setStartDaysAgo(1);
      setEndDaysAgo(0);
      setSelectedDayOfWeek('?');
      setSelectedHour('8');
      setSelectedMinute('0');
      setSelectedCampaign(null);
      setSelectedScorecard(null);
      setEditingJobId(null);
      setCallsPerAgent('');
      setSamplingScope('range');
      clearAllFilters();
      fetchJobs();
    } catch (error) {
      setAlert({ variant: 'error', heading: 'Error', text: 'Failed to save scheduled job.' });
    } finally {
      setCreatingJob(false);
    }
  };

  const handleDeleteJob = (jobId) => {
    setConfirmDialog({
      title: 'Delete Scheduled Job',
      message: 'Are you sure you want to delete this scheduled job?',
      onConfirm: () => doDeleteJob(jobId),
    });
  };

  const doDeleteJob = async (jobId) => {
    setConfirmDialog(null);
    try {
      console.log('Deleting job with ID:', jobId);
      await deleteScheduledJobApi(jobId);
      setAlert({ variant: 'success', heading: 'Deleted', text: 'Job deleted successfully.' });
      fetchJobs();
    } catch (error) {
      setAlert({ variant: 'error', heading: 'Error', text: 'Failed to delete job.' });
    }
  };

  const formatCron = (cron) => {
    if (!cron) return 'N/A';
    const match = cron.match(/cron\((.*?)\)/);
    if (!match) return cron;

    const [minute, utcHour, , , dow] = match[1].split(' ');
    
    // Convert UTC Hour to SAST (+2)
    let sastHour = (parseInt(utcHour, 10) + 2) % 24;
    
    // Handle Day Wrap for display
    // If SAST hour is smaller than UTC hour, it means we wrapped forward to the next day
    let displayDow = dow;
    if (dow !== '?' && sastHour < parseInt(utcHour, 10)) {
      const currentIndex = DAYS.indexOf(dow);
      displayDow = DAYS[(currentIndex + 1) % 7];
    }

    const time = `${sastHour.toString().padStart(2, '0')}:${minute.padStart(2, '0')} SAST`;
    
    const dayMap = {
      'SUN': 'Sundays', 'MON': 'Mondays', 'TUE': 'Tuesdays', 'WED': 'Wednesdays',
      'THU': 'Thursdays', 'FRI': 'Fridays', 'SAT': 'Saturdays', '?': 'Daily'
    };

    return `${dayMap[displayDow] || 'Daily'} at ${time}`;
  };

  const getDynamicDateLabel = (daysAgo) => {
    const offset = parseInt(daysAgo, 10);
    if (isNaN(offset)) return "...";
    
    const d = new Date();
    d.setDate(d.getDate() - offset);
    
    if (offset === 0) return "Today";
    if (offset === 1) return "Yesterday";

    return d.toLocaleDateString('en-ZA', { 
      weekday: 'long', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const handleEditClick = (job) => {
    setEditingJobId(job.jobId || job.id);
    setJobName(job.jobName);
    
    // 1. Set Campaign & Scorecard (Updated to use 'template' as per your data structure)
    const campaignObj = campaigns.find(c => c.value === job.campaign);
    setSelectedCampaign(campaignObj || { label: job.campaign, value: job.campaign });
    
    // Important: Use job.template here instead of job.scorecard
    setSelectedScorecard({ label: job.template, value: job.template });

    // 2. Parse Cron back to SAST UI
    const match = job.scheduleExpression.match(/cron\((.*?)\)/);
    if (match) {
      const [minute, utcHour, , , dow] = match[1].split(' ');
      setSelectedMinute(minute);
      // Convert UTC back to SAST (+2)
      const sastHour = (parseInt(utcHour, 10) + 2) % 24;
      // If the hour wrapped past midnight (UTC 22/23 -> SAST 00/01), the stored
      // cron day is the previous day; advance it so the UI shows the SAST day
      // (mirrors formatCron, and generateCronExpression wraps it back on save).
      let uiDow = dow;
      if (dow !== '?' && sastHour < parseInt(utcHour, 10)) {
        uiDow = DAYS[(DAYS.indexOf(dow) + 1) % 7];
      }
      setSelectedDayOfWeek(uiDow);
      setSelectedHour(sastHour.toString());
    }

    // 3. Set Lookback Window
    if (job.lookback) {
      setStartDaysAgo(job.lookback.startDaysAgo.toString());
      setEndDaysAgo(job.lookback.endDaysAgo.toString());
    }

    // 4. Set Filters (FIX: Hydrate with default values to prevent .map() errors)
    setFilters({
      direction: job.filters?.direction || '',
      userIds: job.filters?.userIds || [],
      queueIds: job.filters?.queueIds || [],
      wrapUpCodes: job.filters?.wrapUpCodes || [],
      divisionIds: job.filters?.divisionIds || [],
      ani: job.filters?.ani || '',
      dnis: job.filters?.dnis || ''
    });

    // 4b. Set per-agent sampling
    setCallsPerAgent(job.callsPerAgent ? job.callsPerAgent.toString() : '');
    setSamplingScope(job.samplingScope || 'range');

    // 5. Scroll to top so user sees the form is populated
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setEditingJobId(null);
    setJobName('');
    setStartDaysAgo(1);
    setEndDaysAgo(0);
    setSelectedDayOfWeek('?');
    setSelectedHour('8');
    setSelectedMinute('0');
    setSelectedCampaign(null);
    setSelectedScorecard(null);
    setCallsPerAgent('');
    setSamplingScope('range');
    clearAllFilters();
  };


  return (
    <div className="space-y-6">
      <ConfirmationModal
        open={!!confirmDialog}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        onClose={() => setConfirmDialog(null)}
        onConfirm={confirmDialog?.onConfirm}
      />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Scheduled Genesys Downloads</h1>
          <p className="text-gray-600 mt-1">Manage automated batch fetches triggered via EventBridge</p>
        </div>
      </div>

      <div className="card">
        <div className="flex items-center gap-2.5 mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Active Schedules</h2>
          {!loadingJobs && scheduledJobs.length > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-cloudza-50 text-cloudza-700 text-xs font-semibold">
              {scheduledJobs.length}
            </span>
          )}
        </div>
        {loadingJobs ? (
          <LoadingState variant="rows" header={false} label="Loading schedules" />
        ) : scheduledJobs.length === 0 ? (
          <div className="py-10 px-6 text-center">
            <span className="mx-auto w-12 h-12 rounded-2xl bg-cloudza-50 text-cloudza-700 flex items-center justify-center">
              <FaCalendarAlt className="text-lg" />
            </span>
            <p className="mt-4 text-sm font-semibold text-gray-800">No schedules yet</p>
            <p className="mt-1 text-sm text-gray-400">Configure one below and it will run automatically via EventBridge.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Job Name</th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Created By</th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Campaign</th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Scorecard</th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Schedule</th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Filters</th>
                  <th className="px-5 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
              {scheduledJobs.map((job) => (
                <tr key={job.jobId} className={`hover:bg-cloudza-50/40 transition-colors ${editingJobId === (job.jobId || job.id) ? 'bg-cloudza-50/40' : ''}`}>
                  {/* COLUMN 1: JOB & TEMPLATE */}
                  <td className="px-5 py-3 align-top">
                    <div className="text-sm font-semibold text-gray-900">{job.jobName}</div>
                    {editingJobId === (job.jobId || job.id) && (
                      <span className="text-[11px] text-cloudza-600 font-medium">Editing…</span>
                    )}
                  </td>

                  <td className="px-5 py-3 align-top text-sm text-gray-600">
                    {job.createdBy}
                  </td>

                  {/* COLUMN 2: CAMPAIGN */}
                  <td className="px-5 py-3 align-top text-sm text-gray-600">
                    {job.campaign}
                  </td>

                  <td className="px-5 py-3 align-top text-sm text-gray-600">
                    {job.template}
                  </td>

                  {/* COLUMN 3: SCHEDULE & LOOKBACK */}
                  <td className="px-5 py-3 align-top">
                    <div className="text-sm font-medium text-gray-900">
                      {formatCron(job.scheduleExpression)}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      Window: {job.lookback?.startDaysAgo}–{job.lookback?.endDaysAgo} days ago
                    </div>
                  </td>

                  {/* COLUMN 4: ACTIVE FILTERS */}
                  <td className="px-5 py-3 align-top">
                    <div className="flex flex-wrap gap-1 max-w-xs">
                      {/* Direction Badge */}
                      {job.filters?.direction && (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded-lg border border-gray-200">
                          {job.filters.direction.toUpperCase()}
                        </span>
                      )}

                      {/* Queues Badge */}
                      {job.filters?.queueIds?.length > 0 && (
                        <span className="px-2 py-0.5 bg-coral-50 text-coral-700 text-[10px] font-medium rounded-md border border-coral-200">
                          {job.filters.queueIds.length} Queue{job.filters.queueIds.length === 1 ? '' : 's'}
                        </span>
                      )}

                      {/* Agents Badge */}
                      {job.filters?.userIds?.length > 0 && (
                        <span className="px-2 py-0.5 bg-green-50 text-green-700 text-[10px] font-medium rounded-md border border-green-200">
                          {job.filters.userIds.length} Agent{job.filters.userIds.length === 1 ? '' : 's'}
                        </span>
                      )}

                      {/* Wrap-up Badge */}
                      {job.filters?.wrapUpCodes?.length > 0 && (
                        <span className="px-2 py-0.5 bg-purple-50 text-purple-700 text-[10px] font-medium rounded-md border border-purple-200">
                          {job.filters.wrapUpCodes.length} Wrap-up{job.filters.wrapUpCodes.length === 1 ? '' : 's'}
                        </span>
                      )}

                      {/* Generic Filter Info if no specific IDs are mapped */}
                      {!job.filters || Object.keys(job.filters).length === 0 ? (
                        <span className="text-xs text-gray-400 italic">No filters applied</span>
                      ) : null}
                    </div>
                  </td>

                  {/* COLUMN 5: ACTIONS */}
                  <td className="px-5 py-3 align-top text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => handleEditClick(job)}
                        className="p-2 rounded-lg text-gray-400 hover:text-cloudza-600 hover:bg-cloudza-50 transition-colors"
                        title="Edit Filters & Schedule"
                        aria-label={`Edit ${job.jobName}`}
                      >
                        <FaPen className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDeleteJob(job.jobId)}
                        className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        title="Delete Schedule"
                        aria-label={`Delete ${job.jobName}`}
                      >
                        <FaTrash className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card space-y-6" data-tour="schedule-form">
        <div className="border-b border-gray-100 pb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {editingJobId ? `Edit Schedule: ${jobName}` : 'Create New Schedule'}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">Follow the steps below to configure a safe and efficient automated job.</p>
          </div>
          {editingJobId && (
            <button onClick={resetForm} className="btn-outline !py-1.5 !px-3.5 !text-xs">
              <FaTimes />
              Cancel Editing
            </button>
          )}
        </div>

        {/* STEP 1: Basic Config */}
        <div>
          <h3 className="text-md font-bold text-gray-800 mb-4 flex items-center">
            <span className="bg-cloudza-600 text-white w-6 h-6 rounded-full inline-flex items-center justify-center text-xs font-semibold mr-2 flex-shrink-0">1</span> 
            Basic Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Schedule Name</label>
              <input type="text" value={jobName} onChange={(e) => setJobName(e.target.value)} placeholder="e.g. DailyMTN" maxLength={10} className="input-field w-full" />
              <p className="text-xs text-gray-400 mt-1">{jobName.length}/10 characters</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Campaign</label>
              <SelectField value={selectedCampaign?.value || ''} onChange={(e) => setSelectedCampaign(campaigns.find(c => c.value === e.target.value))} className="select-field w-full">
                <option value="" disabled>Select a campaign</option>
                {campaigns.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </SelectField>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Scorecard</label>
              <SelectField value={selectedScorecard?.value || ''} onChange={(e) => setSelectedScorecard(scorecards.find(s => s.value === e.target.value))} className="select-field w-full" disabled={!selectedCampaign}>
                <option value="" disabled>Select a scorecard</option>
                {scorecards.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </SelectField>
            </div>
          </div>
        </div>

        {/* STEP 2: Run Frequency */}
        <div>
          <h3 className="text-md font-bold text-gray-800 mb-4 flex items-center">
            <span className="bg-cloudza-600 text-white w-6 h-6 rounded-full inline-flex items-center justify-center text-xs font-semibold mr-2 flex-shrink-0">2</span> 
            Run Frequency (SAST)
          </h3>
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Repeat On</label>
                <SelectField value={selectedDayOfWeek} onChange={(e) => setSelectedDayOfWeek(e.target.value)} className="select-field w-full !text-sm">
                  <option value="?">Every Day</option>
                  <option value="MON">Mondays</option>
                  <option value="TUE">Tuesdays</option>
                  <option value="WED">Wednesdays</option>
                  <option value="THU">Thursdays</option>
                  <option value="FRI">Fridays</option>
                  <option value="SAT">Saturdays</option>
                  <option value="SUN">Sundays</option>
                </SelectField>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Hour (SAST)</label>
                <SelectField value={selectedHour} onChange={(e) => setSelectedHour(e.target.value)} className="select-field w-full !text-sm">
                  {[...Array(24).keys()].map(h => <option key={h} value={h}>{h.toString().padStart(2, '0')}:00</option>)}
                </SelectField>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Minute</label>
                <SelectField value={selectedMinute} onChange={(e) => setSelectedMinute(e.target.value)} className="select-field w-full !text-sm">
                  {Array.from({ length: 12 }, (_, i) => i * 5).map((m) => <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>)}
                </SelectField>
              </div>
            </div>
          </div>
        </div>

        {/* STEP 3: Relative Time Window */}
        <div>
          <h3 className="text-md font-bold text-gray-800 mb-4 flex items-center">
            <span className="bg-cloudza-600 text-white w-6 h-6 rounded-full inline-flex items-center justify-center text-xs font-semibold mr-2 flex-shrink-0">3</span> 
            Data Extraction Window
          </h3>
          <div className={`p-5 rounded-lg border space-y-4 transition-colors duration-300 ${hasOverlapRisk ? 'bg-amber-50 border-amber-200' : isInvalidRange ? 'bg-red-50 border-red-200' : 'bg-cloudza-50 border-cloudza-100'}`}>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                  Scan From ({getDynamicDateLabel(startDaysAgo)})
                </label>
                <div className="relative">
                  <input type="number" min="1" value={startDaysAgo} onChange={(e) => setStartDaysAgo(e.target.value)} className="input-field w-full !pr-16 !text-sm" />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">days ago</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">
                  Scan Until ({getDynamicDateLabel(endDaysAgo)})
                </label>
                <div className="relative">
                  <input type="number" min="0" value={endDaysAgo} onChange={(e) => setEndDaysAgo(e.target.value)} className="input-field w-full !pr-16 !text-sm" />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">days ago</span>
                </div>
              </div>
            </div>

            {/* DYNAMIC GUARDRAIL WARNINGS */}
            <div className="pt-3 border-t border-black/5 mt-2">
              {hasInvalidNumbers ? (
                <p className="text-sm text-red-600 font-semibold flex items-center gap-2">
                  <FaExclamationTriangle className="flex-shrink-0" /> Enter a number of days for both "Scan From" and "Scan Until".
                </p>
              ) : isInvalidRange ? (
                <p className="text-sm text-red-600 font-semibold flex items-center gap-2">
                  <FaExclamationTriangle className="flex-shrink-0" /> Invalid Range: "Scan From" must be further back in time than "Scan Until".
                </p>
              ) : hasOverlapRisk ? (
                <p className="text-sm text-amber-700 font-medium flex items-start gap-2">
                  <FaExclamationTriangle className="flex-shrink-0 mt-0.5" /> 
                  <span>
                    <strong>Overlap Warning:</strong> This job runs every <strong>{frequencyInDays} day(s)</strong>, but extracts <strong>{windowSize} days</strong> of data. This will result in {windowSize - frequencyInDays} day(s) of duplicate data being processed every time it runs.
                  </span>
                </p>
              ) : hasGapRisk ? (
                  <p className="text-sm text-cloudza-700 font-medium flex items-start gap-2">
                    <FaInfoCircle className="flex-shrink-0 mt-0.5" /> 
                    <span>
                      <strong>Data Sampling Mode:</strong> This job runs every <strong>{frequencyInDays} day(s)</strong>, but extracts <strong>{windowSize} day(s)</strong> of data. This means {frequencyInDays - windowSize} day(s) of data between runs will be skipped.
                    </span>
                  </p>
                ) : (
                <p className="text-sm text-green-700 font-semibold flex items-center gap-2">
                  <FaCheckCircle className="flex-shrink-0" /> Perfect Match: Your {windowSize}-day extraction window perfectly aligns with your {frequencyInDays}-day run frequency.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* STEP 4: Per-Agent Sampling */}
        <div>
          <h3 className="text-md font-bold text-gray-800 mb-4 flex items-center">
            <span className="bg-cloudza-600 text-white w-6 h-6 rounded-full inline-flex items-center justify-center text-xs font-semibold mr-2 flex-shrink-0">4</span>
            Per-Agent Sampling (Optional)
          </h3>
          <div className="p-5 rounded-lg border bg-gray-50 border-gray-200 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Calls per agent</label>
              <input
                type="number"
                min="1"
                step="1"
                value={callsPerAgent}
                onChange={(e) => setCallsPerAgent(e.target.value)}
                placeholder="Blank = QA all calls"
                className="input-field w-full !text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">QA only this many calls per agent, sampled evenly across time.</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Sample scope</label>
              <SelectField
                value={samplingScope}
                onChange={(e) => setSamplingScope(e.target.value)}
                disabled={!callsPerAgent}
                className="select-field w-full !text-sm disabled:opacity-60"
              >
                <option value="range">Across whole window</option>
                <option value="day">Per day</option>
              </SelectField>
              <p className="mt-1 text-xs text-gray-500">
                {samplingScope === 'day'
                  ? 'N calls per agent for each day in the extraction window.'
                  : 'N calls per agent over the entire extraction window.'}
              </p>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-100 pt-4">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h3 className="text-sm font-semibold text-gray-900">Fetch Filters (Optional)</h3>
            <button onClick={clearAllFilters} className="text-xs text-cloudza-600 hover:text-cloudza-700 font-medium">Clear All Filters</button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Call Direction</label>
              <SelectField value={filters.direction} onChange={(e) => updateFilter('direction', e.target.value)} className="select-field w-full">
                <option value="">All Directions</option>
                <option value="inbound">Inbound Only</option>
                <option value="outbound">Outbound Only</option>
              </SelectField>
            </div>

            <div className="relative">
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Genesys Queues</label>
              {genesysFilterErrors.queues && (
                <p className="text-xs text-red-600 mb-1">Live queues unavailable: {genesysFilterErrors.queues}</p>
              )}
              <input type="text" className="input-field w-full !text-sm" placeholder={fetchingFilters ? "Loading..." : "Search Queues..."} value={queueSearch} onChange={(e) => { setQueueSearch(e.target.value); setIsQueueOpen(true); }} onFocus={() => setIsQueueOpen(true)} />
              {isQueueOpen && queueSearch && (
                <div className="absolute z-50 w-full bg-white border mt-1 rounded-lg shadow-xl max-h-40 overflow-y-auto dropdown-in">
                  {queueOptions.filter(q => q.name.toLowerCase().includes(queueSearch.toLowerCase())).map(queue => (
                    <div key={queue.id} className="p-2 hover:bg-gray-50 cursor-pointer text-sm" onClick={() => { if (!filters.queueIds.includes(queue.id)) updateFilter('queueIds', [...filters.queueIds, queue.id]); setQueueSearch(''); setIsQueueOpen(false); }}>{queue.name}</div>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-1 mt-2">
                {filters.queueIds.map((id, i) => (
                  <span key={id} className="bg-coral-100 text-coral-700 px-2 py-1 rounded-full text-xs flex items-center">
                    {queueOptions.find(q => q.id === id)?.name || id}
                    <button onClick={() => removeFromArrayFilter('queueIds', i)} className="ml-1.5 opacity-60 hover:opacity-100 font-bold" aria-label="Remove">×</button>
                  </span>
                ))}
              </div>
            </div>

            <div className="relative">
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Agents</label>
              {genesysFilterErrors.agents && (
                <p className="text-xs text-red-600 mb-1">Live agents unavailable: {genesysFilterErrors.agents}</p>
              )}
              <input type="text" className="input-field w-full !text-sm" placeholder="Search Agents..." value={agentSearch} onChange={(e) => { setAgentSearch(e.target.value); setIsAgentOpen(true); }} onFocus={() => setIsAgentOpen(true)} />
              {isAgentOpen && agentSearch && (
                <div className="absolute z-50 w-full bg-white border mt-1 rounded-lg shadow-xl max-h-40 overflow-y-auto dropdown-in">
                  {agentOptions.filter(a => a.name.toLowerCase().includes(agentSearch.toLowerCase())).map(agent => (
                    <div key={agent.id} className="p-2 hover:bg-green-50 cursor-pointer text-sm" onClick={() => { if (!filters.userIds.includes(agent.id)) updateFilter('userIds', [...filters.userIds, agent.id]); setAgentSearch(''); setIsAgentOpen(false); }}>{agent.name}</div>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-1 mt-2">
                {filters.userIds.map((id, i) => (
                  <span key={id} className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs flex items-center">
                    {agentOptions.find(a => a.id === id)?.name || id}
                    <button onClick={() => removeFromArrayFilter('userIds', i)} className="ml-1.5 opacity-60 hover:opacity-100 font-bold" aria-label="Remove">×</button>
                  </span>
                ))}
              </div>
            </div>

            <div className="relative">
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Wrap-Up Codes</label>
              {genesysFilterErrors.wrapUpCodes && (
                <p className="text-xs text-red-600 mb-1">Live wrap-up codes unavailable: {genesysFilterErrors.wrapUpCodes}</p>
              )}
              <input type="text" className="input-field w-full !text-sm" placeholder="Search Codes..." value={wrapupSearch} onChange={(e) => { setWrapupSearch(e.target.value); setIsWrapupOpen(true); }} onFocus={() => setIsWrapupOpen(true)} />
              {isWrapupOpen && wrapupSearch && (
                <div className="absolute z-50 w-full bg-white border mt-1 rounded-lg shadow-xl max-h-40 overflow-y-auto dropdown-in">
                  {wrapUpCodeOptions.filter(c => c.name.toLowerCase().includes(wrapupSearch.toLowerCase())).map(code => (
                    <div key={code.id} className="p-2 hover:bg-gray-50 cursor-pointer text-sm" onClick={() => { if (!filters.wrapUpCodes.includes(code.id)) updateFilter('wrapUpCodes', [...filters.wrapUpCodes, code.id]); setWrapupSearch(''); setIsWrapupOpen(false); }}>{code.name}</div>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-1 mt-2">
                {filters.wrapUpCodes.map((id, i) => (
                  <span key={id} className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs flex items-center">
                    {wrapUpCodeOptions.find(c => c.id === id)?.name || id}
                    <button onClick={() => removeFromArrayFilter('wrapUpCodes', i)} className="ml-1.5 opacity-60 hover:opacity-100 font-bold" aria-label="Remove">×</button>
                  </span>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">ANI (Caller Number)</label>
                <input type="text" value={filters.ani} onChange={(e) => updateFilter('ani', e.target.value)} placeholder="tel:+1234567890" className="input-field w-full" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-1.5">DNIS (Dialed Number)</label>
                <input type="text" value={filters.dnis} onChange={(e) => updateFilter('dnis', e.target.value)} placeholder="tel:+0987654321" className="input-field w-full" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1.5">Division IDs</label>
              <div className="flex gap-2">
                <input type="text" value={divisionIdInput} onChange={(e) => setDivisionIdInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && addToArrayFilter('divisionIds', divisionIdInput, setDivisionIdInput)} placeholder="Enter division ID and press Enter" className="input-field flex-1" />
                <button onClick={() => addToArrayFilter('divisionIds', divisionIdInput, setDivisionIdInput)} className="btn-primary flex-shrink-0"><FaPlus /> Add</button>
              </div>
              {filters.divisionIds.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {filters.divisionIds.map((id, index) => (
                    <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                      {id}
                      <button onClick={() => removeFromArrayFilter('divisionIds', index)} className="ml-1.5 opacity-60 hover:opacity-100 font-bold" aria-label="Remove">×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="pt-4 border-t border-gray-100">
          <button
            onClick={handleCreateSchedule}
            disabled={creatingJob || !selectedCampaign || !selectedScorecard || !jobName}
            className={editingJobId ? 'btn-warning' : 'btn-primary'}
          >
            {editingJobId ? <FaSave /> : <FaPlus />}
            {creatingJob ? 'Saving...' : editingJobId ? 'Update Scheduled Job' : 'Create Scheduled Job'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduledJobs;